// Unpublished Work © 2020 Deere & Company.

import {datadogRum} from '@datadog/browser-rum';
import {redirectToLoginPage} from './sessions';

export const ErrorFallbackMessage = 'Error: No error set';

const UNAUTHORIZED = 401;

type ErrorResponse = {
    message?: string;
};

function isErrorResponse(value: unknown): value is ErrorResponse {
    return typeof value === 'object' && value !== null && 'message' in value;
}

function reportResponseFormatError(response: Response, error: Error) {
    if (!datadogRum.getInitConfiguration()) {
        return;
    }

    datadogRum.addError(error, {
        contentType: response.headers.get('content-type'),
        responseUrl: response.url,
        status: response.status,
    });
}

export async function handleResponse<T>(
    response: Response,
    customError?: string,
): Promise<T> {
    const text = await response.text();
    let result: unknown;
    let parseError: Error | undefined;

    if (text) {
        try {
            result = JSON.parse(text);
        } catch (error) {
            parseError = error as Error;
        }
    }

    if (!response.ok) {
        if (response.status === UNAUTHORIZED) {
            redirectToLoginPage();
        }
        const message =
            (isErrorResponse(result) && result.message) ||
            customError ||
            ErrorFallbackMessage;

        throw new Error(message);
    }

    if (parseError) {
        const error = new Error(
            customError || 'The server returned an invalid response format.',
            {cause: parseError},
        );
        reportResponseFormatError(response, error);

        throw error;
    }

    return result as T;
}
